import type { ArenaRunDto } from './types';

interface TaskResultProps {
  run: ArenaRunDto;
  onApprove: (runId: string) => void;
  onRetry: (taskId: string) => void;
  onValidate: (runId: string) => void;
  approving: boolean;
}

export default function TaskResult({ run, onApprove, onRetry, onValidate, approving }: TaskResultProps) {
  const isPassed = run.status === 'passed' || run.status === 'completed';
  const isFailed = run.status === 'failed' || run.status === 'error';

  return (
    <div className="space-y-4">
      {/* Status header */}
      <div className={`px-4 py-3 rounded-lg border ${
        isPassed ? 'bg-green-50 border-green-200 text-green-800' :
        isFailed ? 'bg-red-50 border-red-200 text-red-800' :
        'bg-blue-50 border-blue-200 text-blue-800'
      }`}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">
            {isPassed ? '✅ Passed' : isFailed ? '❌ Failed' : 'ℹ️ ' + run.status.toUpperCase()}
          </span>
          <span className="text-xs font-mono opacity-75">{run.id}</span>
        </div>
        {run.errorMessage && (
          <p className="text-xs mt-2 font-mono bg-white/50 rounded p-2">{run.errorMessage}</p>
        )}
      </div>

      {/* Score */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-lg font-bold text-gray-700">{(run.score * 100).toFixed(0)}%</div>
          <div className="text-xs text-gray-500">Score</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-lg font-semibold text-green-600">{run.passedCount}</div>
          <div className="text-xs text-gray-500">Passed</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-lg font-semibold text-red-600">{run.failedCount}</div>
          <div className="text-xs text-gray-500">Failed</div>
        </div>
      </div>

      {/* Validation criteria */}
      {run.validationResults.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-600 mb-2">Validation Criteria</h4>
          <div className="space-y-1">
            {run.validationResults.map((vr, i) => (
              <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs ${
                vr.passed ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}>
                <span>{vr.passed ? '✓' : '✗'}</span>
                <span className="flex-1">{vr.description}</span>
                {!vr.passed && (
                  <span className="font-mono text-[10px] opacity-75">expected: {vr.expected}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agent actions log */}
      {run.agentActions.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-600 mb-2">Agent Actions ({run.agentActions.length})</h4>
          <div className="bg-[#1a1a2e] rounded-lg p-3 font-mono text-xs max-h-64 overflow-y-auto">
            {run.agentActions.map((action) => (
              <div key={action.step} className="mb-2">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-blue-400 font-bold">Step {action.step}</span>
                  <span className="text-amber-300">{action.tool}</span>
                </div>
                {action.input && (
                  <div className="text-gray-400 ml-4 whitespace-pre-wrap break-all text-[11px]">
                    {action.input.substring(0, 200)}
                  </div>
                )}
                {action.result && (
                  <div className="text-green-300 ml-4 whitespace-pre-wrap break-all text-[11px]">
                    {action.result.substring(0, 200)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2">
        {isPassed && (
          <button
            onClick={() => onApprove(run.id)}
            disabled={approving}
            className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100 disabled:opacity-50"
          >
            {approving ? 'Approving...' : 'Approve'}
          </button>
        )}
        {isFailed && (
          <button
            onClick={() => onRetry(run.taskId)}
            className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded hover:bg-amber-100"
          >
            Retry
          </button>
        )}
        <button
          onClick={() => onValidate(run.id)}
          className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100"
        >
          Re-validate
        </button>
      </div>

      {/* Meta */}
      <div className="text-xs text-gray-400 space-y-0.5 pt-2 border-t border-gray-100">
        <div>Started: {new Date(run.startedAt).toLocaleString()}</div>
        {run.completedAt && <div>Completed: {new Date(run.completedAt).toLocaleString()}</div>}
        <div>Duration: {(run.durationMs / 1000).toFixed(1)}s</div>
      </div>
    </div>
  );
}
